/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import * as React from 'react';
import { compose, withState, withHandlers, withProps, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { formValueSelector, change } from 'redux-form';
import { Row, Col } from 'react-flexbox-grid';
import { get, map } from 'lodash';
import Search from 'material-ui/svg-icons/action/search';
import Security from 'material-ui/svg-icons/hardware/security';
import PersonIcon from 'material-ui/svg-icons/social/person';
import ActionList from 'material-ui/svg-icons/action/list';
import FileThumb from 'material-ui/svg-icons/action/account-box';

import { DateField, InputField, Input, AutoCompleteField, AutoComplete, Date } from '@components/form';
import { sexEnum, typeEnum } from '@components/person/_personEnums';
import { ModalButton } from '@components/modalButton/ModalButton';
import { LocalLookupsForm } from '@components/person/LocalLookupsForm';
import { ExternalLookupsForm } from '@components/person/ExternalLookupsForm';
import { localLookupPerson, externalLookupPerson } from '@modules/wantedPersons/_wantedPersonsActions';
import { IszrLookupsForm } from './IszrLookupsForm';
import { DelictsByOffenderForm } from './DelictsByOffenderForm';
import { withNotifications } from '@components/hoc/withNotifications';
import { iszrFindPhoto } from '@modules/wantedPersons/_wantedPersonsActions';
import { Loader } from '@components/loader';
import { getConfig } from '@components/provider/configuration/_configurationActions';
import { PcrPersonForm } from './PcrPersonForm';
import { birthNumberToBirthDate } from '@utils/utils';
import { format } from 'date-fns';

export const enhance = compose<any, any>(
  withState('localLookups', 'handleLocalLookups', []),
  withState('externalLookups', 'handleExternalLookups', []),
  withState('crvLookupsVersion', 'handleCrvLookupsVersion', 0),
  withState('loader', 'handleLoader', false),
  withNotifications({ iszrFindPhoto, getConfig }),
  connect<any>(
    (state, { form, formName = '' }: any) => {
      return {
        firstName: formValueSelector(form)(state, `${formName}firstName`),
        lastName: formValueSelector(form)(state, `${formName}lastName`),
        address: formValueSelector(form)(state, `${formName}address`),
        birthDate: formValueSelector(form)(state, `${formName}birthDate`),
        birthNumber: formValueSelector(form)(state, `${formName}birthNumber`),
        sex: formValueSelector(form)(state, `${formName}sex`),
        aifo: formValueSelector(form)(state, `${formName}aifo`),
        identifications: formValueSelector(form)(state, `${formName}identifications`),
        citizenship: formValueSelector(form)(state, `${formName}citizenship`),
        photo: formValueSelector(form)(state, 'photo'),
        photoTmp: formValueSelector(form)(state, 'photoTmp')
      };
    },
    (dispatch, { form, formName = '' }: any) => ({
      changeFieldValue: (label, value) => dispatch(change(form, `${formName}${label}`, value)),
      changePhoto: value => dispatch(change(form, 'photoTmp', value)),
      changePhotoBase64: value => dispatch(change(form, 'photoTmp.image', value))
    })
  ),
  lifecycle<any, {}, {}>({
    async componentDidMount() {
      const { getConfigNotify } = this.props;
      try {
        const config = await getConfigNotify();
        this.setState({ config });
      } catch {
        this.setState({ config: {} });
      }
    }
  }),
  withProps(
    ({
      firstName,
      lastName,
      birthDate,
      photo,
      photoTmp,
      withMaidenName,
      withPhone,
      config,
      citizenship,
      birthNumber,
      changeFieldValue
    }) => {
      return {
        initialValuesIszr: {
          firstName,
          lastName,
          birthDate,
          birthNumber
        },
        inputFields: [
          { name: 'firstName', label: 'Jméno', onBlur: undefined },
          { name: 'lastName', label: 'Přijmení', onBlur: undefined },
          ...(withMaidenName ? [{ name: 'maidenName', label: 'Rodné příjmení', onBlur: undefined }] : []),
          ...(withPhone ? [{ name: 'phone', label: 'Telefon', onBlur: undefined }] : []),
          {
            name: 'birthNumber',
            label: 'Rodné číslo',
            onBlur: e => {
              if (e) {
                const birthNumberr = e.target.value;
                const birthDatee = birthNumberToBirthDate(birthNumberr);
                if (birthDatee) {
                  changeFieldValue('birthDate', format(birthDatee, 'YYYY-MM-DD'));
                }
              }
            }
          }
        ],
        initialValuesDelict: {
          firstName,
          lastName,
          birthDate
        },
        initialValuesPcr: {
          firstName,
          lastName,
          birthDate,
          citizenship
        },
        photoBase64: get(photoTmp, 'image'),
        photoId: get(photo, 'id'),
        iszrPhotoEnable: get(config, 'extensions.iszr-photo'),
        isPcrIszr: get(config, 'extensions.pcr-iszr') === 'true'
      };
    }
  ),
  withHandlers({
    incrementCrvLookupsVersion: ({ crvLookupsVersion, handleCrvLookupsVersion }) => () => {
      handleCrvLookupsVersion(crvLookupsVersion + 1);
    },
    searchLocalLookups: ({ handleLocalLookups, firstName, lastName, address, birthDate, birthNumber }) => async () => {
      const response = await localLookupPerson({ firstName, lastName, address, birthNumber, birthDate });
      handleLocalLookups(response);
    },
    searchExternalLookups: ({ handleExternalLookups, firstName, lastName, sex }) => async () => {
      const response = await externalLookupPerson({ firstName, lastName, sex });
      handleExternalLookups(response);
    },
    downloadIszrPhoto: ({
      iszrFindPhotoNotify,
      firstName,
      lastName,
      birthDate,
      changePhoto,
      photoBase64,
      photoId,
      aifo,
      iszrPhotoEnable,
      handleLoader
    }) => async () => {
      if (photoBase64 || photoId || !(aifo && iszrPhotoEnable)) {
        return;
      }
      handleLoader(true);
      const response = await iszrFindPhotoNotify({ firstName, lastName, birthDate, aifo });
      changePhoto(response);
      handleLoader(false);
    }
  })
);

const Person: React.SFC<any> = ({
  inputFields,
  localLookups,
  externalLookups,
  searchLocalLookups,
  searchExternalLookups,
  initialValuesIszr,
  initialValuesDelict,
  initialValuesPcr,
  changeFieldValue,
  changePhoto,
  changePhotoBase64,
  incrementCrvLookupsVersion,
  showCompanyInputs,
  withNote,
  withPhoto,
  disabled,
  photoBase64,
  photoId,
  downloadIszrPhoto,
  aifo,
  iszrPhotoEnable,
  loader,
  isPcrIszr
}) => (
  <React.Fragment>
    {loader && <Loader />}
    <Row>
      <Col md={12}>
        <Row>
          {map(
            [
              {
                label: 'Místní pátrání',
                icon: <Search />,
                items: localLookups,
                style: { marginLeft: 10, marginBottom: 10, marginRight: 10 },
                ModalComponent: LocalLookupsForm,
                onClick: searchLocalLookups
              },
              {
                label: 'Lustrace ISZR',
                icon: <PersonIcon />,
                style: { marginBottom: 10, marginRight: 10 },
                ModalComponent: IszrLookupsForm,
                initialValues: initialValuesIszr,
                changeFieldValue,
                changePhoto,
                withPhoto,
                incrementCrvLookupsVersion,
                dialogStyles: {
                  style: { paddingTop: '10vh' },
                  contentStyle: {
                    paddingTop: 5,
                    transform: 'none'
                  },
                  bodyStyle: { padding: '0', overflowY: 'auto' }
                }
              },
              {
                label: 'Přehled přestupku',
                icon: <ActionList />,
                style: { marginBottom: 10, marginRight: 10 },
                ModalComponent: DelictsByOffenderForm,
                initialValues: initialValuesDelict,
                dialogStyles: {
                  style: { paddingTop: '5vh' },
                  contentStyle: {
                    paddingTop: 10,
                    transform: 'none'
                  },
                  bodyStyle: { padding: '0', overflow: 'auto' }
                }
              }
            ],
            (item, key) => <ModalButton disabled={disabled} key={key} {...item} />
          )}
          {isPcrIszr ? (
            <ModalButton
              disabled={disabled}
              {...{
                label: 'Pátrání PČR',
                icon: <Security />,
                style: { marginBottom: 10, marginRight: 10 },
                ModalComponent: PcrPersonForm,
                initialValues: initialValuesPcr,
                changeFieldValue,
                changePhotoBase64,
                withPhoto,
                dialogStyles: {
                  style: { paddingTop: '5vh' },
                  contentStyle: {
                    paddingTop: 5,
                    minWidth: '85vw',
                    transform: 'none'
                  },
                  bodyStyle: { padding: '0', overflowY: 'auto', maxHeight: '90vh' },
                  autoDetectWindowHeight: false
                }
              }}
            />
          ) : (
            <ModalButton
              disabled={disabled}
              {...{
                label: 'Pátrání PČR',
                icon: <Security />,
                items: externalLookups,
                style: { marginBottom: 10, marginRight: 10 },
                ModalComponent: ExternalLookupsForm,
                onClick: searchExternalLookups
              }}
            />
          )}
        </Row>
      </Col>
      <Col md={12} style={{ marginTop: 32 }}>
        {withPhoto && (
          <>
            <div
              style={{
                height: 280,
                width: 250,
                backgroundColor: '#efefef',
                float: 'left',
                display: 'flex',
                alignItems: 'center',
                cursor: !photoBase64 && !photoId && (aifo && iszrPhotoEnable) ? 'pointer' : 'default'
              }}
              onClick={downloadIszrPhoto}
            >
              {photoBase64 && (
                <>
                  <img
                    src={`data:image/png;base64, ${photoBase64}`}
                    style={{ objectFit: 'contain', maxWidth: '100%' }}
                  />
                </>
              )}
              {!photoBase64 && (
                <>
                  {photoId && (
                    <img
                      src={`/metropolis/api/files/${photoId}`}
                      alt="photo"
                      style={{ objectFit: 'contain', maxWidth: '100%' }}
                    />
                  )}
                  {!photoId && (
                    <FileThumb
                      style={{
                        width: 50,
                        height: 50,
                        display: 'block',
                        margin: '0 auto',
                        color: '#bbb'
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
        {inputFields.map(({ name, label, onBlur }) => (
          <Row key={name}>
            <Col md={12}>
              <InputField component={Input} id={name} name={name} label={label} disabled={disabled} onBlur={onBlur} />
            </Col>
          </Row>
        ))}
        <Row>
          <Col md={12}>
            <DateField component={Date} id="birthDate" name="birthDate" label="Datum narození" disabled={disabled} />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AutoCompleteField
              component={AutoComplete}
              id="sex"
              name="sex"
              label="Pohlaví"
              items={sexEnum}
              fullWidth={true}
              disabled={disabled}
              labelFunction={o => get(o, 'label')}
              valueFunction={o => get(o, 'id')}
            />
          </Col>
        </Row>
        {showCompanyInputs && (
          <Row>
            <Col md={12}>
              <AutoCompleteField
                component={AutoComplete}
                id="type"
                name="type"
                label="Typ osoby"
                items={typeEnum}
                fullWidth={true}
                disabled={disabled}
                labelFunction={o => get(o, 'label')}
                valueFunction={o => get(o, 'id')}
              />
            </Col>
            {[{ name: 'ic', label: 'IČ' }, { name: 'companyName', label: 'Název společnosti' }].map(
              ({ name, label }, i) => (
                <Col md={12} key={i}>
                  <InputField component={Input} id={name} name={name} label={label} disabled={disabled} />
                </Col>
              )
            )}
            {withNote && (
              <Col md={12}>
                <InputField
                  component={Input}
                  id={'note'}
                  name={'note'}
                  label={'Poznámka'}
                  disabled={disabled}
                  multiLine={true}
                />
              </Col>
            )}
          </Row>
        )}
      </Col>
    </Row>
  </React.Fragment>
);

export default enhance(Person);



// WEBPACK FOOTER //
// ./src/components/person/Person.tsx